import { Grid, useTheme } from '@mui/material';
import { useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';

import { PurchaseStatusOptions } from '@/Modules/PurchaseOrdersModule/shared/enums/purchase-status.enum';
import usePurchaseOrdersReportStore from '@/Modules/ReportsModule/PurchaseOrdersReport/shared/store/purchase-orders-reports.store';
import {
  initialPurchaseOrderReportFiltersValues,
  validatePurchaseOrderReportFiltersForm
} from '@/Modules/ReportsModule/PurchaseOrdersReport/shared/utils/helpers/filters-validation';
import Alert from '@/shared/components/core-ui/alert/alert.component';
import Button from '@/shared/components/core-ui/button/button.component';
import { TextInputType } from '@/shared/components/core-ui/controls/text-field/text-field.component';
import FormDatePicker from '@/shared/components/group-ui/form-controls/form-date-picker/form-date-picker.component';
import FormTextInput from '@/shared/components/group-ui/form-controls/form-input-text/form-text-input.component';
import FormRangeSlider from '@/shared/components/group-ui/form-controls/form-range-slider/form-range-slider.component';
import FormMultiSelect from '@/shared/components/group-ui/form-controls/select/multi-select/multi-select.component';
import PageHeader from '@/shared/components/group-ui/page-header/page-header.component';
import { APP_ROUTES } from '@/shared/config/routes-config';
import { useAlert } from '@/shared/hooks/alert/useAlert';
import { useForm } from '@/shared/hooks/form/useForm';
import { useLocalization } from '@/shared/hooks/useLocalization';
import { RouteConfig } from '@/shared/interfaces/routes-config.interface';
import { convertEnumsToIDLabelArr } from '@/shared/services/helpers/format-enums/id-label-format/convert-enum-to-id-label';
import { setLocalStorageItem } from '@/shared/services/utils/local-storage/local-storage.service';
import { PurchaseOrderReportUIFilters } from '@/shared/ui-models/purchase-order-report.ui-model';

function PurchaseOrdersReportFilterContainer(): JSX.Element {
  const theme = useTheme();
  const navigate = useNavigate();
  const { t } = useLocalization();

  const { isOpenAlert, setIsOpenAlert, alertText, showAlert } = useAlert();
  const {
    actions: { setQuery }
  } = usePurchaseOrdersReportStore();

  const { values, errors, setErrors, handleInputChange } = useForm<PurchaseOrderReportUIFilters>(
    initialPurchaseOrderReportFiltersValues,
    false,
    (fieldValues, errors, setErrors) => validatePurchaseOrderReportFiltersForm(fieldValues, errors, setErrors, t)
  );

  const statusSelectOptions = useMemo(() => {
    return convertEnumsToIDLabelArr({ ...PurchaseStatusOptions });
  }, []);

  useEffect(() => {
    if (!values.startDate) {
      return;
    }

    validatePurchaseOrderReportFiltersForm(values, errors, setErrors, t);
  }, [values, t]);

  const handleGenerateReport = (): void => {
    const { valid } = validatePurchaseOrderReportFiltersForm(values, errors, setErrors, t);
    if (!valid) return;

    setLocalStorageItem('purchase-orders-report-filters', values);
    setQuery(values, 0);
    navigate((APP_ROUTES.PURCHASE_ORDERS_REPORT_MODULE.CHILDREN as RouteConfig).LIST.FULL_PATH);
  };

  return (
    <>
      <PageHeader
        data={{
          title: t('REPORTS_MODULE.PURCHASE_ORDERS_REPORT.PAGE_HEADERS.TITLE'),
          description: t('REPORTS_MODULE.PURCHASE_ORDERS_REPORT.PAGE_HEADERS.DESCRIPTION')
        }}
        components={{
          actionsList: []
        }}
      />

      <Grid container>
        <Grid
          container
          sx={{
            backgroundColor: 'white',
            borderRadius: '1rem',
            p: 3,
            m: '2rem 3rem 0',
            [theme.breakpoints.down('sm')]: {
              textAlign: 'center',
              p: 1,
              m: '1rem 1.5rem 0'
            }
          }}
        >
          <Grid container item xs={12} spacing={3}>
            <Grid item xs={12} md={4}>
              <FormDatePicker
                data={{
                  name: 'startDate',
                  label: `${t('REPORTS_MODULE.PURCHASE_ORDERS_REPORT.FORMS.START_DATE')}*`,
                  value: values.startDate,
                  error: errors.startDate
                }}
                eventHandlers={{ handleInputChange }}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <FormDatePicker
                data={{
                  name: 'endDate',
                  label: `${t('REPORTS_MODULE.PURCHASE_ORDERS_REPORT.FORMS.END_DATE')}*`,
                  value: values.endDate,
                  error: errors.endDate
                }}
                eventHandlers={{ handleInputChange }}
              />
            </Grid>
          </Grid>
          <Grid container item xs={12} spacing={3}>
            <Grid item xs={12} md={4}>
              <FormTextInput
                data={{
                  name: 'autoProfitMarginFrom',
                  placeholder: t('REPORTS_MODULE.PURCHASE_ORDERS_REPORT.FORMS.PROFIT_MARGIN_FROM_PLACEHOLDER'),
                  label: t('REPORTS_MODULE.PURCHASE_ORDERS_REPORT.FORMS.PROFIT_MARGIN_FROM'),
                  value: values.autoProfitMarginFrom ?? '',
                  error: errors.autoProfitMarginFrom
                }}
                config={{ type: TextInputType.NUMBER }}
                eventHandlers={{ handleInputChange: handleInputChange }}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <FormTextInput
                data={{
                  name: 'autoProfitMarginTo',
                  placeholder: t('REPORTS_MODULE.PURCHASE_ORDERS_REPORT.FORMS.PROFIT_MARGIN_TO_PLACEHOLDER'),
                  label: t('REPORTS_MODULE.PURCHASE_ORDERS_REPORT.FORMS.PROFIT_MARGIN_TO'),
                  value: values.autoProfitMarginTo ?? '',
                  error: errors.autoProfitMarginTo
                }}
                config={{ type: TextInputType.NUMBER }}
                eventHandlers={{ handleInputChange: handleInputChange }}
              />
            </Grid>
          </Grid>
          <Grid container item xs={12} spacing={3}>
            <Grid item xs={12} md={4}>
              <FormMultiSelect
                data={{
                  name: 'status',
                  label: t('REPORTS_MODULE.PURCHASE_ORDERS_REPORT.FORMS.STATUS'),
                  placeholder: t('REPORTS_MODULE.PURCHASE_ORDERS_REPORT.FORMS.STATUS_PLACEHOLDER'),
                  value: values.status ?? [],
                  error: errors.status,
                  selectOptions: statusSelectOptions,
                  noResultsFoundText: t('REPORTS_MODULE.PURCHASE_ORDERS_REPORT.TABLE.NO_STATUS_FOUND')
                }}
                eventHandlers={{ handleInputChange }}
              />
            </Grid>
          </Grid>
          <Button
            config={{}}
            eventHandlers={{
              handleClick: handleGenerateReport
            }}
          >
            {t('REPORTS_MODULE.PURCHASE_ORDERS_REPORT.FORMS.GENERATE_REPORT')}
          </Button>
        </Grid>
      </Grid>

      <Alert
        data={{ text: alertText }}
        config={{ isOpen: isOpenAlert }}
        eventHandlers={{ setOpen: setIsOpenAlert }}
      />
    </>
  );
}

export default PurchaseOrdersReportFilterContainer;
